import React, { Fragment, useEffect, useState } from "react";
import { Row, Col, Table, Spinner, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchPurchaseOrderId } from "slices/Procurement/purchase-order/purchase-order/thunk";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { MecordColors } from "Constants/Color";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { toast } from "react-toastify";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { DocumentsList } from "components/DocumentListing/DocumentListing";
import { AttachmentList } from "components/DocumentListing/AttachmentListing";
import Tooltip from "@mui/material/Tooltip";
import TruncatedTextWithTooltip from "Common/Components/TruncatedTextWithTooltip";
import { formatIndianAmount } from "helpers/formatIndianAmount";
const PurchaseOrderDetails = ({ id: propId }: { id?: string }) => {
  const { state } = useLocation();
  const { po_id: urlId } = useParams();
  const po_id = propId || urlId;
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [poData, setPoData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const hasPR = poData?.data?.purchase_order_purchase_requests?.some(
    (pr: any) => pr?.purchase_request?.pr_no,
  );

  useEffect(() => {
    if (po_id) {
      setLoading(true);
      dispatch(fetchPurchaseOrderId(po_id))
        .unwrap()
        .then((data: any) => {
          setPoData(data);
        })
        .catch((err: any) => {
          console.error("Error:", err);
          toast.error(err?.message || "Failed to fetch Purchase Order details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, po_id]);

  const displayValue = (val: any) => val ?? "-";

  const content = (
    <>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-3">
                  <strong>PO No. :</strong> {displayValue(poData?.data?.po_no)}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>PO Date :</strong>{" "}
                  <FormattedDate date={poData?.data?.po_date} />
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>PR No. :</strong>{" "}
                  {poData?.data?.purchase_order_purchase_requests?.length > 0
                    ? poData.data.purchase_order_purchase_requests
                        .map((pr: any) => pr?.purchase_request?.pr_no)
                        .filter((v: any) => v != null && v !== "")
                        .join(", ") || "-"
                    : "-"}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>ETA Date :</strong>{" "}
                  <FormattedDate date={poData?.data?.eta_date} />
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Initiated By :</strong>{" "}
                  {displayValue(poData?.data?.initiated_by)}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Tags :</strong>{" "}
                  {Array.isArray(poData?.data?.tags) && poData.data.tags.length > 0 ? (
                    <span className="d-flex flex-wrap gap-2 mt-1">
                      {poData.data.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="badge bg-light text-dark rounded-2"
                          style={{
                            border: "1px solid #dee2e6",
                            padding: "6px 10px",
                            fontSize: "0.8rem",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </span>
                  ) : (
                    "-"
                  )}
                </p>
              </Col>
            </Row>

            <hr />

            <div className="mb-4">
              <h5 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Supplier Details
              </h5>
              <Card
                className="mb-4"
                style={{
                  boxShadow: "none",
                  border: "1px solid #dee2e6",
                  backgroundColor: MecordColors?.LightThemeBGColor,
                }}
              >
                <Card.Body>
                  <Row className="mt-3">
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Supplier Name :</strong>{" "}
                        {displayValue(
                          poData?.data?.supplier_name ||
                            poData?.data?.supplier?.supplier_name,
                        )}
                      </p>
                      <p className="mb-2">
                        <strong>Supplier Type :</strong>{" "}
                        {displayValue(poData?.data?.supplier_type)}
                      </p>
                      <p className="mb-2">
                        <strong>Phone No. :</strong>{" "}
                        {displayValue(
                          poData?.data?.phone_number ||
                            poData?.data?.supplier?.phone_number,
                        )}
                      </p>
                      <p className="mb-2">
                        <strong>Email :</strong>{" "}
                        {displayValue(
                          poData?.data?.email || poData?.data?.supplier?.email,
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Contact Person :</strong>{" "}
                        {displayValue(
                          poData?.data?.contact_person_name ||
                            poData?.data?.supplier?.contact_details?.[0]
                              ?.contact_name,
                        )}
                      </p>
                      <p className="mb-2">
                        <strong>Contact Person Phone No. :</strong>{" "}
                        {displayValue(
                          poData?.data?.contact_person_phone_number ||
                            poData?.data?.supplier?.contact_details?.[0]
                              ?.contact_phone,
                        )}
                      </p>
                      <p className="mb-2">
                        <strong>Contact Person Email :</strong>{" "}
                        {displayValue(
                          poData?.data?.contact_person_email ||
                            poData?.data?.supplier?.contact_details?.[0]
                              ?.contact_email,
                        )}
                      </p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>

            <hr />
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-3">
                  <strong>Bill To :</strong>{" "}
                  {poData?.data?.bill_to?.organization_name
                    ? `${poData.data.bill_to.organization_name} (${
                        poData.data.bill_to.org_address || ""
                      }, ${poData.data.bill_to.org_city || ""}, ${
                        poData.data.bill_to.org_state || ""
                      }, ${poData.data.bill_to.org_country || ""})`
                    : displayValue(poData?.data?.bill_to_name)}
                </p>
              </Col>
              <Col md={6}>
                <p className="mb-3">
                  <strong>Ship To :</strong>{" "}
                  {poData?.data?.ship_to?.warehouse_name
                    ? `${poData.data.ship_to.warehouse_name} (${
                        poData.data.ship_to.address || ""
                      }, ${poData.data.ship_to.city || ""}, ${
                        poData.data.ship_to.state || ""
                      }, ${poData.data.ship_to.country || ""})`
                    : displayValue(poData?.data?.ship_to_name)}
                </p>
              </Col>
            </Row>
            <hr />

            <div className="my-4">
              <h5 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Currency & Exchange Rate
              </h5>
              <Row className="mb-3">
                <Col md={6}>
                  <p className="mb-2">
                    <strong>Currency :</strong>{" "}
                    {poData?.data?.currency
                      ? `${poData.data.currency.currency_code} - ${
                          poData.data.currency.currency_name || ""
                        }`
                      : displayValue(poData?.data?.payment_method)}
                  </p>
                </Col>
                <Col md={6}>
                  <p className="mb-2">
                    <strong>Exchange Rate :</strong>{" "}
                    {displayValue(poData?.data?.exchange_rate)}
                  </p>
                </Col>
              </Row>
            </div>

            <hr />

            <div className="my-4">
              <h5 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Item Details
              </h5>
              <div className="mb-4" style={{ overflowX: "auto" }}>
                <Table responsive className="align-middle text-center">
                  <thead
                    style={{ backgroundColor: MecordColors.LightGrayColor }}
                  >
                    <tr>
                      <th style={{ minWidth: "70px" }}>Sr No.</th>
                      {hasPR && (
                        <th
                          style={{
                            minWidth: "120px",
                            position: "sticky",
                            left: 0,
                            background: MecordColors?.LightGrayColor, // important
                            zIndex: 11,
                          }}
                        >
                          PR No.
                        </th>
                      )}
                      <th
                        style={{
                          minWidth: "120px",
                          position: "sticky",
                          left: hasPR ? "120px" : 0,
                          background: MecordColors?.LightGrayColor, // important
                          zIndex: 11,
                        }}
                      >
                        Product Name
                      </th>
                      <th style={{ minWidth: "120px" }}>Product Code</th>
                      <th style={{ minWidth: "150px" }}>Lead Time (Weeks)</th>
                      <th style={{ minWidth: "120px" }}>Total Quantity</th>
                      <th style={{ minWidth: "140px" }}>Balance Quantity</th>
                      <th style={{ minWidth: "100px" }}>Unit</th>
                      <th style={{ minWidth: "120px" }}>Unit Price</th>
                      <th style={{ minWidth: "140px" }}>Rate in Currency</th>
                      <th style={{ minWidth: "120px" }}>Gross Value</th>
                      <th style={{ minWidth: "100px" }}>GST %</th>
                      <th style={{ minWidth: "100px" }}>CGST %</th>
                      <th style={{ minWidth: "100px" }}>SGST % </th>
                      <th style={{ minWidth: "100px" }}>IGST %</th>
                      <th style={{ minWidth: "120px" }}>GST Amount</th>
                      <th style={{ minWidth: "120px" }}>Total</th>
                      <th style={{ minWidth: "300px" }}>PR Description</th>
                      <th style={{ minWidth: "300px" }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: "#ffffff" }}>
                    {poData?.data?.products &&
                    poData.data.products.length > 0 ? (
                      poData.data.products.map((item: any, index: number) => (
                        <tr key={item.po_product_id || index}>
                          <td>{index + 1}</td>
                          {hasPR && (
                            <td
                              style={{
                                position: "sticky",
                                left: 0,
                                background: "white", // important
                                zIndex: 11,
                              }}
                            >
                              {displayValue(item.purchase_request?.pr_no)}
                            </td>
                          )}
                          <td
                            style={{
                              position: "sticky",
                              left: hasPR ? "120px" : 0,
                              background: "white", // important
                              zIndex: 11,
                            }}
                          >
                            <TruncatedTextWithTooltip
                              text={displayValue(
                                item.product_name || item.item_name,
                              )}
                              maxWidth="150px"
                            />
                          </td>
                          <td>{displayValue(item.product_code)}</td>
                          <td>{displayValue(item.delivery_lead_time)}</td>
                          <td>
                            {displayValue(item.order_quantity || item.quantity)}
                          </td>{" "}
                          <td>{displayValue(item.balance_quantity || 0)}</td>
                          <td>{displayValue(item.unit_name)}</td>
                          <td>₹ {displayValue(item.unit_price)}</td>
                          <td>
                            {poData?.data?.currency?.currency_symbol}{" "}
                            {item.rate_in_currency}
                          </td>
                          <td>
                            {poData?.data?.currency?.currency_symbol}{" "}
                            {item.net_taxable_value}
                          </td>
                          <td>{displayValue(item.gst_percent || item.gst)}%</td>
                          <td>
                            {displayValue(item.cgst_percent || item.cgst)}%
                          </td>
                          <td>
                            {displayValue(item.sgst_percent || item.sgst)}%
                          </td>
                          <td>
                            {displayValue(item.igst_percent || item.igst)}%
                          </td>
                          <td>
                            {poData?.data?.currency?.currency_symbol}{" "}
                            {(item.net_taxable_value * item.gst_percent) / 100}
                          </td>
                          <td>
                            {poData?.data?.currency?.currency_symbol}{" "}
                            {item.total}
                          </td>
                          <td>
                            <Tooltip
                              title={displayValue(item.description)}
                              arrow
                              placement="top"
                            >
                              <span
                                style={{
                                  maxWidth: "250px",
                                  display: "inline-block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  verticalAlign: "middle",
                                  cursor: "pointer",
                                }}
                              >
                                {displayValue(item.description)}
                              </span>
                            </Tooltip>
                          </td>
                          <td>
                            <TruncatedTextWithTooltip
                              text={item.remark}
                              maxWidth="250px"
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={13} className="text-center">
                          No Data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            <hr />

            <Row className="my-4 align-items-stretch mb-3">
              {/* Terms & Condition, Notes & Attachment Section */}
              <Col md={6}>
                <Col className="mb-3" md={12}>
                  <h5 className="mb-3">Terms & Conditions</h5>
                  <div
                    className="p-3"
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      border: "1px solid #eee",
                    }}
                  >
                    {(() => {
                      const terms = poData?.data?.terms_and_conditions;

                      if (!terms || terms.length === 0) {
                        return (
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "#999",
                              textAlign: "center",
                              padding: "10px 0",
                            }}
                          >
                            No Terms & Conditions added.
                          </div>
                        );
                      }
                      // Group terms by type to handle multiple options per type
                      const groupedTerms: Record<string, string[]> = {};
                      poData?.data?.terms_and_conditions?.forEach(
                        (item: any) => {
                          if (item.type) {
                            if (!groupedTerms[item.type]) {
                              groupedTerms[item.type] = [];
                            }
                            // Handle both string and array options
                            if (Array.isArray(item.options)) {
                              item.options.forEach((opt: string) => {
                                if (
                                  opt &&
                                  !groupedTerms[item.type].includes(opt)
                                ) {
                                  groupedTerms[item.type].push(opt);
                                }
                              });
                            } else if (item.options) {
                              // If options is a string, check if it's newline-separated
                              const options = item.options
                                .split("\n")
                                .filter((opt: string) => opt.trim());
                              options.forEach((opt: string) => {
                                if (
                                  opt &&
                                  !groupedTerms[item.type].includes(opt)
                                ) {
                                  groupedTerms[item.type].push(opt);
                                }
                              });
                            }
                          }
                        },
                      );

                      return Object.entries(groupedTerms).map(
                        ([type, options], idx) => (
                          <div key={idx} className="mb-3">
                            <strong
                              style={{
                                fontSize: "0.75rem",
                                display: "block",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {type}
                            </strong>
                            <ul
                              style={{
                                fontSize: "0.7rem",
                                color: "#666",
                                marginLeft: "20px",
                                paddingLeft: "0",
                                listStyle: "disc",
                              }}
                            >
                              {options.map((option: string, optIdx: number) => (
                                <li
                                  key={optIdx}
                                  style={{ marginBottom: "4px" }}
                                >
                                  {option}
                                </li>
                              ))}
                            </ul>
                            {idx < Object.keys(groupedTerms).length - 1 && (
                              <hr
                                style={{
                                  width: "100%",
                                  marginTop: "12px",
                                }}
                              />
                            )}
                          </div>
                        ),
                      );
                    })()}
                  </div>
                </Col>

                {/* Bottom Notes & Attachment Section */}
                <Col className="mb-3" md={12}>
                  <div>
                    <h6
                      className="fw-semibold mb-3"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Notes & Attachments
                    </h6>
                    <div
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "6px",
                        padding: "12px",
                        height: "60%",
                      }}
                    >
                      {/* Terms and Condition Row */}
                      {/* <Row className="pb-2">
                        <Col md={4} className="fw-semibold">
                          Terms and Conditions
                        </Col>
                        <Col md={7}>
                          {displayValue(poData?.data?.terms_and_conditions)}
                        </Col>
                      </Row>

                      <hr className="my-2" /> */}

                      {/* Notes Row */}
                      <Row className="pb-2">
                        <Col md={4} className="fw-semibold">
                          Notes
                        </Col>
                        <Col style={{ whiteSpace: "pre-wrap" }} md={7}>
                          {displayValue(poData?.data?.notes)}
                        </Col>
                      </Row>

                      <hr className="my-2" />

                      {/* Attachment Row */}
                      <Row className="pt-2">
                        <Col md={4} className="fw-semibold">
                          Attachments
                        </Col>

                        <Col md={7}>
                          {/* <AttachmentList attachments={poData.data.attachments} /> */}

                          {poData?.data?.attachments?.length ? (
                            <AttachmentList
                              attachments={poData.data.attachments}
                            />
                          ) : (
                            "Document Not Uploaded"
                          )}
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
              </Col>

              {/* RIGHT SIDE */}

              {poData?.data?.subtotal !== undefined && (
                <Col md={6}>
                  <h5
                    className="fw-semibold mb-3"
                    style={{ fontSize: "0.9rem" }}
                  >
                    Total Summary
                  </h5>

                  <div
                    style={{
                      backgroundColor: MecordColors?.LightThemeBGColor,
                      padding: "20px",
                      borderRadius: "8px",
                      border: `1px solid ${MecordColors?.LightThemeBGColor}`,
                    }}
                  >
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal</span>
                      <span>
                        {poData?.data?.currency?.currency_symbol}{" "}
                        {formatIndianAmount(poData?.data?.subtotal)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Total GST Value</span>
                      <span>
                        {poData?.data?.currency?.currency_symbol}{" "}
                        {formatIndianAmount(poData?.data?.total_gst_value)}
                      </span>
                    </div>

                    <div
                      className="d-flex justify-content-between pt-2 mt-2"
                      style={{ borderTop: "1px solid #dee2e6" }}
                    >
                      <span className="fw-bold">Grand Amount</span>
                      <span className="fw-bold">
                        {poData?.data?.currency?.currency_symbol}{" "}
                        {formatIndianAmount(poData?.data?.total)}
                      </span>
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {!propId && (
              <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
                <CancelButton
                  onClick={() => navigate(-1)}
                  // redirectTo="/purchase-management/purchase-order"
                  disabled={loading}
                  showConfirmation={false}
                />
              </div>
            )}
          </>
        )}
    </>
  );

  return propId ? (
    <div className="p-0">{content}</div>
  ) : (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title="View Purchase Order"
          titleLink="/purchase-management/purchase-order"
          breadcrumbs={[
            {
              label: "Purchase Order",
              to: "/purchase-management/purchase-order",
            },
            { label: "View Purchase Order", to: "" },
          ]}
          backNavigationPath={-1}
        />
        <hr />
        {content}
      </PageContainer>
    </Fragment>
  );
};

export default PurchaseOrderDetails;
